import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BillingProvider } from "@/context/BillingContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Retainers from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import Snapshots from "@/pages/Snapshots";
import Admin from "@/pages/Admin";
import { Privacy, Terms, Contact } from "@/pages/Legal";
import AuthPage from "@/pages/AuthPage";
import SampleDashboard from "@/pages/SampleDashboard";
import MarketingPage from "@/components/MarketingPage";
import { PAGES as MARKETING_PAGES } from "@/lib/marketingContent";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import "@/App.css";

function PricingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Pricing />;
  if (user) {
    return (
      <AppLayout>
        <Pricing />
      </AppLayout>
    );
  }
  return <Pricing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<PricingRoute />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      {/* Email + password auth (additive — Google OAuth still works). */}
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/forgot-password" element={<AuthPage mode="forgot" />} />
      <Route path="/reset-password" element={<AuthPage mode="reset" />} />
      <Route path="/sample-dashboard" element={<SampleDashboard />} />
      {/* SEO marketing pages (data-driven). Routes here MUST appear before any catch-alls. */}
      {Object.keys(MARKETING_PAGES).map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<MarketingPage slug={slug} />} />
      ))}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/retainers"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Retainers />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/retainers/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ClientDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      {/* Backwards-compat: old /clients URLs */}
      <Route path="/clients" element={<Navigate to="/retainers" replace />} />
      <Route path="/clients/:id" element={<NavigateClientDetail />} />
      {/* Email CTAs link to /billing — route it to Settings (Billing card lives there). */}
      <Route path="/billing" element={<Navigate to="/settings" replace />} />
      <Route
        path="/snapshots"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Snapshots />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Settings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function NavigateClientDetail() {
  const path = window.location.pathname.replace(/^\/clients\//, "/retainers/");
  return <Navigate to={path} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <BillingProvider>
            <AppRouter />
            <Toaster position="top-right" />
          </BillingProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
